import { useState, useEffect, useCallback } from "react";
import { Loader2, Building2, ShieldCheck, Link2Off, Send, BookUser, Layers3 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import i18n, { countryName } from "../i18n";
import { intlLocaleFor } from "../i18n/locales";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const PROPOSE_FIELDS = [
  { v: "email", l: "Mon e-mail" },
  { v: "phone", get l() { return tr("directory:user.monTelephone"); } },
  { v: "first_name", get l() { return tr("directory:user.monPrenom"); } },
  { v: "last_name", l: "Mon nom" },
  { v: "display_name", get l() { return tr("directory:user.monNomAffiche"); } },
];

const PLAN_LABEL = {
  free: "Gratuit",
  pro: "Pro",
  team: "Team",
  business: "Business",
  enterprise: "Entreprise",
};

const MEMBERSHIP_STATUS_LABEL = {
  active: "Actif",
  suspended: "Suspendu",
  pending: "En attente",
};

const fmtDate = (value) => value
  ? new Date(value).toLocaleDateString(intlLocaleFor(i18n.language))
  : "—";

const ProposeControl = ({ contactId, onDone }) => {
  const [field, setField] = useState("email");
  const [busy, setBusy] = useState(false);
  const propose = async () => {
    setBusy(true);
    try {
      await api.post(`/account/directories/${contactId}/propose`, { field });
      toast.success(tr("directory:user.propositionEnvoyeeAL"));
      onDone?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Proposition impossible.");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <Select value={field} onValueChange={setField}>
        <SelectTrigger data-testid={`propose-field-${contactId}`} className="h-9 w-40 bg-slate-50 border-slate-200 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {PROPOSE_FIELDS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" data-testid={`propose-btn-${contactId}`} disabled={busy} onClick={propose} className="border-slate-200">
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}{tr("directory:user.proposer")}
      </Button>
    </div>
  );
};

export const UserDirectories = () => {
  const { workspaces, activeWorkspaceId, switchWorkspace, loadWorkspaces } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/directories");
      setItems(data);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Rafraîchit aussi la liste des memberships afin que société, pays, rôle, forfait et date
  // reflètent toujours les données canoniques du Workspace.
  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);

  const openWorkspace = (workspaceId) => {
    switchWorkspace(workspaceId);
    window.location.assign("/compte?tab=overview");
  };

  const unlink = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/account/directories/${id}/unlink`);
      toast.success(tr("directory:user.vousEtesDesolidariseDe"));
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Action impossible.");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6" data-testid="profile-workspaces">
        <div className="flex items-center gap-2 mb-2">
          <Layers3 className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-lg font-semibold">Mes Workspaces</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Espaces auxquels votre compte appartient. Le rôle et la date d’arrivée sont propres à chaque Workspace.
        </p>
        {workspaces.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">Aucun Workspace accessible.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="profile-workspaces-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-3 pr-4 font-semibold">Réf.</th>
                  <th className="py-3 pr-4 font-semibold">Workspace</th>
                  <th className="py-3 pr-4 font-semibold">Rôle</th>
                  <th className="py-3 pr-4 font-semibold">Date d’arrivée</th>
                  <th className="py-3 pr-4 font-semibold">Forfait</th>
                  <th className="py-3 pr-4 font-semibold">Pays</th>
                  <th className="py-3 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {workspaces.map((w) => {
                  const active = w.id === activeWorkspaceId;
                  const status = w.status || "active";
                  return (
                    <tr key={w.id} data-testid={`profile-workspace-${w.id}`} className="border-b border-slate-100 last:border-0">
                      <td className="py-3 pr-4 font-mono-token text-xs text-slate-600 whitespace-nowrap">{w.public_ref || "—"}</td>
                      <td className="py-3 pr-4">
                        <button
                          type="button"
                          onClick={() => openWorkspace(w.id)}
                          className="text-left font-medium text-emerald-700 hover:text-emerald-600 hover:underline"
                          data-testid={`profile-workspace-open-${w.id}`}
                        >
                          {w.name || w.company_name || "Workspace"}
                        </button>
                        {w.company_name && w.company_name !== w.name && (
                          <div className="text-xs text-slate-500 mt-0.5">{w.company_name}</div>
                        )}
                        {active && (
                          <Badge variant="outline" className="mt-1 ml-0 bg-emerald-500/10 text-emerald-700 border-emerald-500/30 text-[10px]">Actif</Badge>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-slate-600">{w.role_label || w.role || "—"}</td>
                      <td className="py-3 pr-4 text-slate-600 whitespace-nowrap">{fmtDate(w.joined_at)}</td>
                      <td className="py-3 pr-4 text-slate-600">{PLAN_LABEL[w.plan] || w.plan || "—"}</td>
                      <td className="py-3 pr-4 text-slate-600 whitespace-nowrap">
                        {w.country_code ? countryName(w.country_code, i18n.language) : "—"}
                      </td>
                      <td className="py-3 text-slate-600">
                        <Badge variant="outline" className={status === "active"
                          ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                          : "bg-slate-100 text-slate-600 border-slate-300"}>
                          {MEMBERSHIP_STATUS_LABEL[status] || status}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-6" data-testid="user-directories">
        <div className="flex items-center gap-2 mb-4">
          <BookUser className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-lg font-semibold">{tr("directory:user.mesAnnuaires")}</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          {tr("directory:user.organisationsQuiReferencentVotre")}
        </p>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div data-testid="user-directories-empty" className="text-sm text-slate-400 py-4">
            {tr("directory:user.aucuneOrganisationNeVous")}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((d) => (
              <div key={d.contact_id} data-testid={`user-directory-${d.contact_id}`} className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                      <Building2 className="w-4 h-4 text-slate-400" />{d.organization || "Organisation"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.email}{d.phone ? ` · ${d.phone}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {d.email_verified && <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30"><ShieldCheck className="w-3 h-3 mr-1" />{tr("directory:user.eMailVerifie")}</Badge>}
                    {d.link_state === "unlinked_by_user" && <Badge variant="outline" className="text-slate-500 border-slate-300">{tr("directory:user.desolidarise")}</Badge>}
                  </div>
                </div>
                {d.link_state !== "unlinked_by_user" && (
                  <div className="flex items-center justify-between gap-2 flex-wrap mt-3 pt-3 border-t border-slate-100">
                    <ProposeControl contactId={d.contact_id} onDone={load} />
                    <Button size="sm" variant="outline" data-testid={`unlink-directory-${d.contact_id}`} disabled={busyId === d.contact_id} onClick={() => unlink(d.contact_id)} className="border-red-200 text-red-600 hover:bg-red-50">
                      {busyId === d.contact_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Link2Off className="w-3.5 h-3.5 mr-1" />}{tr("directory:user.meDesolidariser")}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDirectories;
